import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

/**
 * LivestreamPlayer Component
 * HLS video player for live stream preview using hls.js
 */
const LivestreamPlayer = ({ playbackUrl, isLive = false, className = '' }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!playbackUrl || !videoRef.current) {
            setLoading(false);
            return;
        }

        const video = videoRef.current;

        // Check if HLS is supported
        if (Hls.isSupported()) {
            // Destroy previous instance if exists
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }

            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 90,
            });

            hlsRef.current = hls;

            hls.loadSource(playbackUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setLoading(false);
                if (isLive) {
                    video.play().catch((err) => {
                        console.error('Autoplay prevented:', err);
                    });
                }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Error:', data);
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            setError('Network error - please check your connection');
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            setError('Media error - attempting recovery');
                            hls.recoverMediaError();
                            break;
                        default:
                            setError('Fatal error - cannot play stream');
                            hls.destroy();
                            break;
                    }
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support (Safari)
            video.src = playbackUrl;
            video.addEventListener('loadedmetadata', () => {
                setLoading(false);
                if (isLive) {
                    video.play().catch((err) => {
                        console.error('Autoplay prevented:', err);
                    });
                }
            });

            video.addEventListener('error', () => {
                setError('Error loading video stream');
                setLoading(false);
            });
        } else {
            setError('HLS is not supported in this browser');
            setLoading(false);
        }

        // Cleanup
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [playbackUrl, isLive]);

    if (!playbackUrl) {
        return (
            <div className={`flex items-center justify-center bg-black text-white ${className}`}>
                <p className="text-sm opacity-50">No playback URL available</p>
            </div>
        );
    }

    return (
        <div className={`relative bg-black ${className}`}>
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                    <p className="text-sm opacity-50">Loading player...</p>
                </div>
            )}

            {error && (
                <div className="absolute inset-0 flex items-center justify-center text-white bg-black/80">
                    <p className="text-sm text-red-400">{error}</p>
                </div>
            )}

            <video
                ref={videoRef}
                controls
                className="w-full h-full"
                playsInline
                muted={false}
            />

            {!isLive && !loading && !error && (
                <div className="absolute top-4 right-4">
                    <span className="bg-gray-700/80 text-white px-3 py-1 rounded-full text-xs">
                        Offline
                    </span>
                </div>
            )}

            {isLive && !loading && !error && (
                <div className="absolute top-4 right-4">
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                        LIVE
                    </span>
                </div>
            )}
        </div>
    );
};

export default LivestreamPlayer;
